'use client'

/**
 * RedditPostsBoard — the top posts across the trading subreddits for analysis, with three sort toggles:
 * Hot (upvote velocity with time decay — the closest proxy to "most viewed", which Reddit's API does not
 * expose), Most Upvoted, and Most Commented. Each title links straight to the Reddit thread so you can
 * read and comment; cashtags link to our stock hubs. Fed by getRedditRadar().posts.
 */

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { ArrowBigUp, MessageSquare, Flame, ExternalLink } from 'lucide-react'
import type { RedditPost, RedditRadar } from '@/lib/dashboard/redditRadar'

type SortKey = 'hot' | 'upvotes' | 'comments'
const SORTS: { key: SortKey; label: string; hint: string }[] = [
  { key: 'hot', label: 'Hot', hint: 'upvote velocity with time decay, the best proxy for "most viewed" (Reddit gives no view counts)' },
  { key: 'upvotes', label: 'Most upvoted', hint: 'highest net upvotes' },
  { key: 'comments', label: 'Most commented', hint: 'most comments, the biggest discussions' },
]

const fmt = (n: number) => (n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k' : String(n))
// Real windows, served from the DB post-history (reddit_posts) via /api/dashboard/reddit-radar/window. Longer
// windows fill in as history accrues (the table starts ~empty, grows every 30-min scan).
const TIMEFRAMES: { label: string; h: number }[] = [
  { label: '1h', h: 1 }, { label: '4h', h: 4 }, { label: '12h', h: 12 }, { label: '1d', h: 24 },
  { label: '3d', h: 72 }, { label: '7d', h: 168 }, { label: '30d', h: 720 },
]
function ago(createdUtc: number): string {
  if (!createdUtc) return ''
  const s = Math.max(0, Date.now() / 1000 - createdUtc)
  if (s < 3600) return `${Math.round(s / 60)}m`
  if (s < 86400) return `${Math.round(s / 3600)}h`
  return `${Math.round(s / 86400)}d`
}
// Reddit-style hotness: upvotes decayed by age. A brand-new post pulling upvotes fast beats an old giant.
const hotness = (p: RedditPost) => (p.upvotes + p.comments * 0.5) / Math.pow((Math.max(0, Date.now() / 1000 - (p.createdUtc || 0)) / 3600) + 2, 1.5)

export default function RedditPostsBoard({ initial, subs = [] }: { initial: RedditRadar; subs?: readonly string[] }) {
  const [sort, setSort] = useState<SortKey>('hot')
  const [tfH, setTfH] = useState<number>(24) // timeframe window in hours; default 1 day (owner)
  const [ddOnly, setDdOnly] = useState(false) // show only real DD / analysis posts, not memes
  // Per-subreddit filter. `null` = all subs shown; otherwise a set (lowercased) of the subs to keep. Persisted per browser.
  const [picked, setPicked] = useState<Set<string> | null>(null)

  // DB-backed window (posts + per-sub counts for the selected timeframe). Fetched on every window change; the
  // SSR `initial` snapshot is the instant fallback while it loads / if the fetch fails.
  const [win, setWin] = useState<RedditRadar | null>(null)
  const [winLoading, setWinLoading] = useState(false)
  useEffect(() => {
    let alive = true
    setWinLoading(true)
    fetch(`/api/dashboard/reddit-radar/window?h=${tfH}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive) setWin(j && Array.isArray(j.posts) ? j : null) })
      .catch(() => { if (alive) setWin(null) })
      .finally(() => { if (alive) setWinLoading(false) })
    return () => { alive = false }
  }, [tfH])
  // The posts to render: the DB window when we have it, else the SSR snapshot (client-filtered to the window).
  const windowPosts: RedditPost[] = useMemo(() => {
    if (win?.posts) return win.posts
    const cutoff = Date.now() / 1000 - tfH * 3600
    return (initial.posts || []).filter((p) => !p.createdUtc || p.createdUtc >= cutoff)
  }, [win, initial.posts, tfH])

  useEffect(() => {
    try {
      const raw = localStorage.getItem('rr:posts:subs')
      if (raw) { const arr = JSON.parse(raw); if (Array.isArray(arr)) setPicked(new Set(arr.map((x) => String(x).toLowerCase()))) }
    } catch { /* ignore */ }
  }, [])
  useEffect(() => {
    try {
      if (picked === null) localStorage.removeItem('rr:posts:subs')
      else localStorage.setItem('rr:posts:subs', JSON.stringify([...picked]))
    } catch { /* ignore */ }
  }, [picked])

  // Every tracked sub (from the poller config) plus any sub that appears in posts, deduped case-insensitively.
  // Counts are WINDOW-AWARE — the number on each chip is how many of that sub's posts fall inside the selected
  // window, so it always matches the list below (previously it counted the whole retained set regardless of
  // window, so a chip could read "37" while the 1-day list showed almost nothing).
  const subInfo = useMemo(() => {
    const counts = new Map<string, { name: string; n: number }>()
    for (const s of subs) { const k = s.toLowerCase(); if (!counts.has(k)) counts.set(k, { name: s, n: 0 }) }
    // Prefer the server's per-sub counts for the window; else count the posts we have.
    if (win?.postsBySub) {
      for (const [name, n] of Object.entries(win.postsBySub)) { const k = name.toLowerCase(); const e = counts.get(k); if (e) e.n = n; else counts.set(k, { name, n }) }
    } else {
      for (const p of windowPosts) { const k = (p.sub || '').toLowerCase(); if (!k) continue; const e = counts.get(k); if (e) e.n += 1; else counts.set(k, { name: p.sub, n: 1 }) }
    }
    return [...counts.values()].sort((a, b) => b.n - a.n || a.name.localeCompare(b.name))
  }, [win, windowPosts, subs])

  const isOn = (name: string) => picked === null || picked.has(name.toLowerCase())
  const toggleSub = (name: string) => {
    const k = name.toLowerCase()
    const all = subInfo.map((s) => s.name.toLowerCase())
    setPicked((prev) => {
      const cur = prev === null ? new Set(all) : new Set(prev)
      if (cur.has(k)) cur.delete(k); else cur.add(k)
      // If everything ended up selected, collapse back to the "all" state (null).
      return cur.size === all.length ? null : cur
    })
  }
  const allOn = picked === null

  const ranked = useMemo(() => {
    // windowPosts is already scoped to the selected timeframe (server-side from the DB history). Filter by the
    // chosen subs, then apply the sort.
    const arr = windowPosts.filter((p) => (picked === null || picked.has((p.sub || '').toLowerCase())) && (!ddOnly || p.isDD))
    if (sort === 'hot') arr.sort((a, b) => hotness(b) - hotness(a))
    else if (sort === 'upvotes') arr.sort((a, b) => b.upvotes - a.upvotes)
    else arr.sort((a, b) => b.comments - a.comments)
    return arr.slice(0, 25)
    // Depend on windowPosts (the DB-window result) so the LIST recomputes when the fetch resolves — previously
    // this listed the stale SSR snapshot while the per-sub COUNT used the fresh window (WSB chip "3" over a
    // list of 25), and a short window still showed hours-old snapshot posts.
  }, [windowPosts, sort, picked, ddOnly])

  const when = initial.at ? new Date(initial.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null

  return (
    <div>
      {/* Subreddit filter — every subreddit we scan, click to show/hide. Green dot = included. */}
      {subInfo.length > 0 && (
        <div className="mb-3 rounded-xl border border-zinc-800/70 bg-zinc-900/40 p-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-0.5 text-[11px] font-bold uppercase tracking-wider text-zinc-500">Subreddits</span>
            <button onClick={() => setPicked(null)} disabled={allOn}
              className={`rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${allOn ? 'border-orange-500/50 bg-orange-500/15 text-orange-200' : 'border-zinc-700 bg-zinc-900/60 text-zinc-400 hover:text-zinc-200'}`}>All</button>
            {subInfo.map((s) => {
              const on = isOn(s.name)
              return (
                <button key={s.name} onClick={() => toggleSub(s.name)} title={on ? `Hide r/${s.name}` : `Show r/${s.name}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${on ? 'border-orange-500/40 bg-orange-500/10 text-orange-200' : 'border-zinc-700/70 bg-zinc-900/60 text-zinc-500'}`}>
                  <span className={`h-2 w-2 shrink-0 rounded-full ${on ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                  <span className={on ? '' : 'line-through'}>r/{s.name}</span>
                  <span className="tabular-nums opacity-60">{s.n}</span>
                </button>
              )
            })}
          </div>
          <p className="mt-1.5 text-[10.5px] leading-snug text-zinc-500">Every subreddit we scan. Click one to show or hide its posts. Want another added? Just tell us and we&apos;ll start tracking it.</p>
        </div>
      )}

      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">Window</span>
        {TIMEFRAMES.map((t) => (
          <button key={t.h} onClick={() => setTfH(t.h)} title={`Posts from the last ${t.label}`}
            className={`rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${tfH === t.h ? 'border-orange-500/60 bg-orange-500/15 text-orange-200' : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">Sort</span>
        {SORTS.map((s) => (
          <button key={s.key} onClick={() => setSort(s.key)} title={s.hint}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${sort === s.key ? 'border-orange-500/60 bg-orange-500/15 text-orange-200' : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'}`}>
            {s.key === 'hot' && <Flame className="h-3 w-3" />}{s.label}
          </button>
        ))}
        <button onClick={() => setDdOnly((v) => !v)} title="Show only real DD / analysis posts, not one-line memes"
          className={`ml-1 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${ddOnly ? 'border-violet-500/60 bg-violet-500/15 text-violet-200' : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'}`}>
          DD only
        </button>
      </div>
      <p className="mb-3 text-[11px] text-zinc-600">{winLoading ? 'Loading window…' : `${allOn ? `Top ${Math.min(25, ranked.length)} of ${windowPosts.length}` : `Showing ${ranked.length}`} post${ranked.length === 1 ? '' : 's'} ${allOn ? 'across the tracked subreddits' : 'from your selected subreddits'} in the last ${TIMEFRAMES.find((t) => t.h === tfH)?.label ?? `${tfH}h`}.`}{when ? ` Updated ${when}.` : ''}</p>
      {ranked.length ? (
        <ol className="space-y-1.5">
          {ranked.map((p, i) => <Row key={p.url} p={p} rank={i + 1} />)}
        </ol>
      ) : (
        <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-12 text-center text-sm text-zinc-500">
          {winLoading ? 'Loading…' : windowPosts.length ? 'No posts match the selected subreddits. Turn some back on above.' : 'No posts in this window yet. Try a longer window, or check back as history builds.'}
        </div>
      )}
    </div>
  )
}

function Row({ p, rank }: { p: RedditPost; rank: number }) {
  const t = ago(p.createdUtc)
  return (
    <li className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-3 transition-colors hover:border-zinc-700/80">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 w-5 shrink-0 text-right text-[13px] font-bold tabular-nums text-zinc-600">{rank}</span>
        <div className="min-w-0 flex-1">
          <a href={p.url} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-1.5 text-[14px] font-semibold leading-snug text-zinc-100 hover:text-amber-300">
            <span className="min-w-0">{p.title}</span>
            <ExternalLink className="mt-1 h-3 w-3 shrink-0 text-zinc-600 group-hover:text-amber-300" />
          </a>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
            <span className="font-semibold text-zinc-500">r/{p.sub}</span>
            {t && <span>{t} ago</span>}
            <span className="inline-flex items-center gap-0.5 tabular-nums text-zinc-400"><ArrowBigUp className="h-3.5 w-3.5 text-orange-400" />{fmt(p.upvotes)}</span>
            <span className="inline-flex items-center gap-0.5 tabular-nums text-zinc-400"><MessageSquare className="h-3 w-3 text-sky-400" />{fmt(p.comments)}</span>
            {p.upvoteRatio > 0 && <span className="tabular-nums text-emerald-400/80">{Math.round(p.upvoteRatio * 100)}% upvoted</span>}
            {p.isDD && <span className="rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold text-violet-300 ring-1 ring-violet-500/30" title="Real DD / analysis, not a one-line meme">DD</span>}
            {p.flair && <span className="rounded bg-zinc-800/80 px-1.5 py-0.5 text-[10px] font-medium text-zinc-400">{p.flair}</span>}
            {p.tickers.length > 0 && (
              <span className="flex flex-wrap items-center gap-1">
                {p.tickers.map((sym) => (
                  <Link key={sym} href={`/stocks/${encodeURIComponent(sym)}`} className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-bold text-amber-300 hover:bg-amber-500/20">${sym}</Link>
                ))}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}
